// src/pages/Terms.jsx
import { useEffect } from 'react';
import { motion } from 'framer-motion';
import { MainLayout } from 'components/Layout';

export const Terms = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <MainLayout
      title="Terms of Service — Northup"
      description="The terms and conditions that govern your use of Northup products, including JustKnow."
    >
      <div className="mx-auto max-w-4xl pb-24">
        {/* Header */}
        <motion.header
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, ease: [0.23, 1, 0.32, 1] }}
          className="mb-10 space-y-4"
        >
          <span className="inline-block rounded-full border border-slate-700/70 bg-slate-900/60 px-4 py-1 text-xs font-semibold uppercase tracking-[0.21em] text-[#9D5666]">
            Legal
          </span>

          <h1 className="text-3xl font-semibold text-slate-50 sm:text-4xl">Terms of Service</h1>

          <p className="text-sm text-slate-400">Last updated: {new Date().getFullYear()}</p>

          <p className="max-w-2xl text-sm text-slate-400 sm:text-base">
            These Terms of Service (&ldquo;Terms&rdquo;) govern your access to and use of products
            and services provided by Northup Technologies (&ldquo;Northup&rdquo;, &ldquo;we&rdquo;,
            &ldquo;us&rdquo;, or &ldquo;our&rdquo;), including the JustKnow application and related
            websites and APIs. By accessing or using our services, you agree to be bound by these
            Terms.
          </p>
        </motion.header>

        {/* Quick nav */}
        <motion.nav
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="mb-10 rounded-2xl border border-slate-800/70 bg-slate-900/50 p-5 text-sm backdrop-blur"
        >
          <p className="mb-3 text-xs font-semibold uppercase tracking-[0.18em] text-slate-400">
            In these terms
          </p>
          <ul className="space-y-2 text-slate-300">
            <li>
              <a href="#acceptance" className="hover:text-[#9D5666]">
                1. Acceptance of terms
              </a>
            </li>
            <li>
              <a href="#eligibility" className="hover:text-[#9D5666]">
                2. Eligibility
              </a>
            </li>
            <li>
              <a href="#accounts" className="hover:text-[#9D5666]">
                3. Accounts & security
              </a>
            </li>
            <li>
              <a href="#use-of-service" className="hover:text-[#9D5666]">
                4. Use of the services
              </a>
            </li>
            <li>
              <a href="#payments" className="hover:text-[#9D5666]">
                5. Payments, fares & orders
              </a>
            </li>
            <li>
              <a href="#content" className="hover:text-[#9D5666]">
                6. User content
              </a>
            </li>
            <li>
              <a href="#disclaimers" className="hover:text-[#9D5666]">
                7. Disclaimers & limitation of liability
              </a>
            </li>
            <li>
              <a href="#termination" className="hover:text-[#9D5666]">
                8. Termination
              </a>
            </li>
            <li>
              <a href="#changes" className="hover:text-[#9D5666]">
                9. Changes to these terms
              </a>
            </li>
            <li>
              <a href="#law" className="hover:text-[#9D5666]">
                10. Governing law
              </a>
            </li>
            <li>
              <a href="#contact" className="hover:text-[#9D5666]">
                11. Contact us
              </a>
            </li>
          </ul>
        </motion.nav>

        <div className="space-y-10 text-sm leading-relaxed text-slate-300">
          <Section id="acceptance" title="1. Acceptance of terms">
            <p>
              By accessing or using our services, you confirm that you have read, understood, and
              agree to be bound by these Terms and our Privacy Policy. If you do not agree, you may
              not use our services.
            </p>
          </Section>

          <Section id="eligibility" title="2. Eligibility">
            <p>
              You may use our services only if you are at least 18 years old and legally capable of
              entering into a binding contract. By using our services, you represent and warrant
              that you meet these requirements.
            </p>
          </Section>

          <Section id="accounts" title="3. Accounts & security">
            <p>
              To access certain features, you may need to create an account and provide accurate,
              complete information. You are responsible for:
            </p>
            <ul className="mt-3 list-disc space-y-2 pl-5 text-slate-400">
              <li>Maintaining the confidentiality of your login credentials.</li>
              <li>
                All activities that occur under your account, whether or not authorized by you.
              </li>
            </ul>
            <p className="mt-3">
              You agree to notify us promptly of any unauthorized access or suspected security
              breach.
            </p>
          </Section>

          <Section id="use-of-service" title="4. Use of the services">
            <p>You agree not to misuse our services. In particular, you shall not:</p>
            <ul className="mt-3 list-disc space-y-2 pl-5 text-slate-400">
              <li>Use the services for any unlawful purpose.</li>
              <li>Harass, harm, or impersonate other users, businesses, or riders.</li>
              <li>
                Interfere with or disrupt the operation of the services or underlying
                infrastructure.
              </li>
              <li>
                Reverse engineer, probe, or attempt to gain unauthorized access to any systems.
              </li>
              <li>
                Use automated scripts or bots to access or interact with the services without
                permission.
              </li>
            </ul>
          </Section>

          <Section id="payments" title="5. Payments, fares & orders">
            <p>
              Certain features of the services, such as placing orders or requesting trips, may
              involve payments. By using these features, you agree to:
            </p>
            <ul className="mt-3 list-disc space-y-2 pl-5 text-slate-400">
              <li>
                Provide accurate payment information and authorize us or our payment partners to
                charge applicable fees.
              </li>
              <li>
                Understand that displayed fares or prices may be estimates and can change based on
                distance, time, or dynamic pricing.
              </li>
              <li>
                Comply with any cancellation, refund, or dispute policies we make available within
                the app.
              </li>
            </ul>
            <p className="mt-3">
              Additional terms may apply to business accounts, riders, or partner arrangements.
            </p>
          </Section>

          <Section id="content" title="6. User content">
            <p>
              You may be able to create, upload, or share content (such as reviews, posts, or
              profile information) through our services. You retain ownership of your content, but
              grant Northup a non-exclusive, worldwide, royalty-free license to use, store, display,
              reproduce, and distribute such content as necessary to operate and improve the
              services.
            </p>
            <p className="mt-3">
              You are responsible for ensuring that your content does not violate any laws or
              infringe any third-party rights.
            </p>
          </Section>

          <Section id="disclaimers" title="7. Disclaimers & limitation of liability">
            <p>
              Our services are provided on an &ldquo;AS IS&rdquo; and &ldquo;AS AVAILABLE&rdquo;
              basis. To the fullest extent permitted by law, we disclaim all warranties, whether
              express or implied, including merchantability, fitness for a particular purpose, and
              non-infringement.
            </p>
            <p className="mt-3">
              To the maximum extent allowed by law, Northup and its affiliates shall not be liable
              for any indirect, incidental, special, consequential, or punitive damages, or any loss
              of profits or revenues, arising from or related to your use of the services.
            </p>
          </Section>

          <Section id="termination" title="8. Termination">
            <p>
              We may suspend or terminate your access to the services at any time, with or without
              notice, if we believe you have violated these Terms, pose a risk to other users, or
              for any other reasonable business reason.
            </p>
            <p className="mt-3">
              You may stop using the services at any time. Certain provisions of these Terms will
              survive termination, including those regarding intellectual property, limitations of
              liability, and dispute resolution.
            </p>
          </Section>

          <Section id="changes" title="9. Changes to these terms">
            <p>
              We may modify these Terms from time to time. When we do, we will update the
              &ldquo;Last updated&rdquo; date and, where appropriate, provide additional notice.
              Your continued use of the services after changes become effective constitutes
              acceptance of the revised Terms.
            </p>
          </Section>

          <Section id="law" title="10. Governing law">
            <p>
              These Terms are governed by and construed in accordance with the laws of the
              jurisdiction in which Northup is incorporated, without regard to conflict of law
              principles. Any disputes arising out of or relating to these Terms or the services
              will be subject to the exclusive jurisdiction of the courts in that jurisdiction.
            </p>
          </Section>

          <Section id="contact" title="11. Contact us">
            <p>If you have questions about these Terms, you can contact us at:</p>
            <div className="mt-3 space-y-1 text-slate-400">
              <p>Uganda Central Kampala</p>
              <p>Central Division Nakasero II Lourdel</p>
              <p>P.O.Box 150570 Kampala GPO 7 Lourdel</p>
              <p>Kampala, Uganda</p>
              <p className="mt-2">Email: legal@northup.ug</p>
            </div>
          </Section>

          <p className="pt-6 text-xs text-slate-500">
            These Terms are provided for general informational purposes and may not reflect all
            legal requirements in your jurisdiction. You should consult independent legal counsel to
            adapt these Terms for your specific needs.
          </p>
        </div>
      </div>
    </MainLayout>
  );
};

const Section = ({ id, title, children }) => (
  <section id={id} className="scroll-mt-24">
    <h2 className="mb-3 text-base font-semibold text-slate-100">{title}</h2>
    <div className="space-y-3">{children}</div>
  </section>
);
